import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, FolderOpen, Check, ThermometerSnowflake, Gauge, Activity } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { scrollToId } from "../lib/site";
import { EASE } from "./Reveal";

const CHECKS = ["Custom Sesuai Kebutuhan", "Material Berkualitas", "Teknisi Berpengalaman", "Harga Kompetitif", "Garansi & After Sales"];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const { content, wa } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const [temp, setTemp] = useState(-25.4);

  useEffect(() => {
    const i = setInterval(() => setTemp(-(24.4 + Math.random() * 2)), 2000);
    return () => clearInterval(i);
  }, []);

  const heroImg = content?.settings?.hero_image;

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden noise-overlay">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        {heroImg && (
          <img src={heroImg} alt="Cold storage industrial modern" className="w-full h-[120%] object-cover scale-110" fetchpriority="high" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/85 to-navy-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/60" />
      </motion.div>
      <div className="absolute inset-0 blueprint-grid opacity-60" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-xs tracking-[0.35em] text-ice uppercase mb-7"
          data-testid="hero-eyebrow"
        >
          Kontraktor Cold Storage — Indonesia
        </motion.p>

        <h1 className="font-display font-bold uppercase leading-[0.92] tracking-tight text-frost text-5xl sm:text-7xl lg:text-8xl max-w-5xl" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Solusi <span className="text-ice">Cold Storage</span></MaskedLine>
          <MaskedLine delay={0.5}>Berkualitas dengan</MaskedLine>
          <MaskedLine delay={0.65}>Harga Terjangkau.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: EASE }}
          className="mt-7 max-w-xl text-base sm:text-lg text-steel leading-relaxed"
          data-testid="hero-subheadline"
        >
          Kami menyediakan jasa pembuatan dan instalasi Cold Storage sesuai kebutuhan bisnis Anda — dengan material berkualitas, sistem pendingin yang efisien, dan harga yang kompetitif.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-cta-whatsapp"
            href={wa()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 bg-ice text-navy-950 font-semibold px-7 py-4 rounded-sm hover:bg-glacier transition-colors duration-200 glow-ice"
          >
            <MessageCircle className="w-5 h-5" />
            Konsultasi Gratis via WhatsApp
          </a>
          <button
            data-testid="hero-cta-portfolio"
            onClick={() => scrollToId("portofolio")}
            className="inline-flex items-center gap-2.5 border border-frost/25 text-frost font-semibold px-7 py-4 rounded-sm hover:border-ice hover:text-ice transition-colors duration-200"
          >
            <FolderOpen className="w-5 h-5" />
            Lihat Portofolio
          </button>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-x-6 gap-y-2.5"
          data-testid="hero-checklist"
        >
          {CHECKS.map((c) => (
            <li key={c} className="flex items-center gap-2 text-sm text-steel">
              <Check className="w-4 h-4 text-ice" strokeWidth={3} />
              {c}
            </li>
          ))}
        </motion.ul>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.4, duration: 0.9, ease: EASE }}
        className="hidden xl:block absolute right-10 bottom-24 z-10 glass-card rounded-md p-5 w-64"
        data-testid="hero-hud"
      >
        <p className="font-mono text-[10px] tracking-[0.25em] text-steel uppercase mb-4">Live Telemetry</p>
        <div className="space-y-3.5">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-xs text-steel"><ThermometerSnowflake className="w-4 h-4 text-glacier" /> Suhu Ruang</span>
            <span className="font-mono text-sm text-ice" data-testid="hud-temperature">{temp.toFixed(1)}°C</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-xs text-steel"><Gauge className="w-4 h-4 text-glacier" /> Tekanan Sistem</span>
            <span className="font-mono text-sm text-frost">Normal</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-xs text-steel"><Activity className="w-4 h-4 text-glacier" /> Status</span>
            <span className="flex items-center gap-1.5 font-mono text-sm text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Optimal
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] text-steel uppercase">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="w-px h-10 bg-gradient-to-b from-ice to-transparent" />
      </motion.div>
    </section>
  );
}
